"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "app/get-query-client";
import dynamic from "next/dynamic";
import { Toaster } from "sonner";
import { useState, useEffect, type CSSProperties } from "react";
import { useAppTheme } from "@/hooks/useAppTheme";

// Sonner's built-in palette is independent of the app theme. Resolve its
// standard toast colours through the same tokens as other floating surfaces.
const TOASTER_STYLE = {
  "--normal-bg": "hsl(var(--popover))",
  "--normal-text": "hsl(var(--popover-foreground))",
  "--normal-border": "hsl(var(--border))",
  "--normal-bg-hover": "hsl(var(--muted))",
  "--normal-border-hover": "hsl(var(--border))"
} as CSSProperties;

// Opt-in with RQ_DEVTOOLS=1 (BRE-7919). next.config `env` always defines the
// flag, so the test folds at build time and the dead branch's import() is never
// compiled; the devtools otherwise land in every route's dev graph. A
// NEXT_PUBLIC_* flag would not fold when unset, and a dynamic() outside the
// ternary would keep the import either way.
const ReactQueryDevtools =
  process.env.RQ_DEVTOOLS === "1"
    ? dynamic(
        () =>
          import("@tanstack/react-query-devtools").then(
            (m) => m.ReactQueryDevtools
          ),
        { ssr: false }
      )
    : null;

export default function QueryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [queryClient] = useState(() => getQueryClient());
  const { isDark } = useAppTheme();

  // Clear reload flag on successful mount
  useEffect(() => {
    sessionStorage.removeItem("chunk-reload");
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {ReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
      {children}
      {/* Toaster must be inside QueryClientProvider so toast components can use hooks */}
      <Toaster
        position="bottom-right"
        data-testid="toast-container"
        theme={isDark ? "dark" : "light"}
        style={TOASTER_STYLE}
      />
    </QueryClientProvider>
  );
}
